import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AbandonDossierGQL,
  AbandonDossierMutationVariables,
  ChangeDossierLanguageGQL,
  ChangeDossierLanguageMutationVariables,
  DossierGQL,
  DossierBySinisterNumberGQL,
  QueryDossierBySinisterNumberArgs,
  DossierQueryVariables,
  MutationDossierAgendaConcurencyArgs,
  SearchDossiersAgendaGQL,
  SearchDossiersAgendaQueryVariables,
  SearchDossiersGQL,
  SearchDossiersQueryVariables,
  SendDossierConcurencyActionGQL,
  UpdateDossierGQL,
  UpdateDossierMutationVariables,
  SaveChatCommentsGQL,
  SaveChatCommentsMutationVariables,
} from '@sc/data-access-graphql';
import {
  Dossier,
  DossierToken,
  PhoneNumber,
  SinisterGlass,
  lang,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DossierHttpService {
  private readonly createDossierAccountUrl = '/api/dossier/account/create';
  private readonly createDossierWithPreDeclarationUrl =
    'api/auth/pre-declaration';
  private readonly createDossierWithOpenArrivalUrl = 'api/auth/open-arrival';
  private readonly getPdfDossierUrl = '/api/dossier/fiche';
  private readonly registerAppCloseURL = '/api/dossier/register-app-close';
  private readonly notifyCustomerURL = '/api/dossier/account/notify';
  private readonly sendCustomerAccessEmailUrl =
    '/api/dossier/customer-access-email';

  constructor(
    private readonly dossierGQL: DossierGQL,
    private readonly dossierBySinisterNumberGQL: DossierBySinisterNumberGQL,
    private readonly searchDossierGQL: SearchDossiersGQL,
    private readonly searchDossiersAgendaGQL: SearchDossiersAgendaGQL,
    private readonly updateDossierGQL: UpdateDossierGQL,
    private readonly changeDossierLanguageGQL: ChangeDossierLanguageGQL,
    private readonly abandonDossierGQL: AbandonDossierGQL,
    private readonly sendDossierConcurencyActGQL: SendDossierConcurencyActionGQL,
    private readonly saveChatCommentsGQL: SaveChatCommentsGQL,
    private readonly httpClient: HttpClient
  ) {}

  fetchDossier(params: DossierQueryVariables): Observable<Dossier> {
    return this.dossierGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.dossier) return response.data.dossier as Dossier;
          else throw new Error('Dossier not found');
        })
      );
  }

  fetchDossierBySinisterNumber(
    params: QueryDossierBySinisterNumberArgs
  ): Observable<Dossier> {
    return this.dossierBySinisterNumberGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response?.data?.dossierBySinisterNumber)
            return response.data.dossierBySinisterNumber as Dossier;
          else throw new Error('Dossier not found');
        })
      );
  }

  updateDossier(params: UpdateDossierMutationVariables): Observable<Dossier> {
    const {
      appointment,
      requestAppointment,
      claim,
      customer,
      id,
      insurer,
      insurerData,
      dossierComments,
      state,
      call,
      searchCriteria,
      policyInformation,
      declarationPage,
      nps,
      abTesting,
    } = params.dossier;

    return this.updateDossierGQL
      .mutate({
        dossier: {
          ...(appointment
            ? {
                appointment: {
                  ...appointment,
                },
              }
            : {}),
          ...(requestAppointment
            ? {
                requestAppointment: {
                  ...requestAppointment,
                },
              }
            : {}),
          ...(claim
            ? {
                claim: {
                  ...claim,
                  documents: claim?.documents
                    ? claim.documents.map((picture) => {
                        return {
                          type: picture.type,
                          name: picture.name,
                          url: picture?.url?.toString(),
                          metadata: picture.metadata,
                        };
                      })
                    : null,
                },
              }
            : {}),
          customer,
          id,
          insurer,
          ...(insurerData ? { insurerData } : {}),
          ...(dossierComments ? { dossierComments } : {}),
          state,
          call,
          searchCriteria,
          policyInformation,
          declarationPage,
          nps,
          abTesting,
        },
      })
      .pipe(
        map((response) => {
          return response.data.updateDossier as Dossier;
        })
      );
  }

  changeDossierLanguage(
    params: ChangeDossierLanguageMutationVariables
  ): Observable<boolean> {
    return this.changeDossierLanguageGQL.mutate(params).pipe(
      map((response) => {
        return response.data.changeDossierLanguage as boolean;
      })
    );
  }

  createDossierAccount(
    dossier: Partial<Dossier>
  ): Observable<Partial<Dossier>> {
    return this.httpClient.post<Partial<Dossier>>(
      this.createDossierAccountUrl,
      { dossier },
      { headers: { requestName: 'dossier-http createDossierAccount' } }
    );
  }

  createDossierWithPreDeclaration(
    licensePlate: string,
    sinisterDate: Date,
    login: string
  ): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(
      this.createDossierWithPreDeclarationUrl,
      { licensePlate, sinisterDate, login },
      { headers: { requestName: 'dossier-http createDossier' } }
    );
  }

  createDossierWithOpenArrival(
    firstname: string,
    lastname: string,
    email: string,
    phone: PhoneNumber,
    licensePlate: string,
    sinisterDate: string,
    businessOrigin?: string,
    captchaResponse?: string,
    selectedLanguage?: string,
    glass?: SinisterGlass
  ): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(
      this.createDossierWithOpenArrivalUrl,
      {
        firstname,
        lastname,
        email,
        phone,
        licensePlate,
        sinisterDate,
        businessOrigin,
        captchaResponse,
        selectedLanguage,
        glass,
      },
      { headers: { requestName: 'dossier-http createDossier' } }
    );
  }

  getPDFDossier(dossierId: string, lang?: lang) {
    return this.httpClient.post(
      this.getPdfDossierUrl,
      {
        id: dossierId,
        lang,
      },
      {
        headers: { requestName: 'dossier-http getPdfDossier' },
        responseType: 'blob',
      }
    );
  }

  searchDossiers(
    params: SearchDossiersQueryVariables
  ): Observable<Partial<Dossier[]>> {
    return this.searchDossierGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.searchDossiers) {
            return response.data.searchDossiers as Partial<Dossier[]>;
          }
          return [];
        })
      );
  }

  searchDossiersAgenda(
    params: SearchDossiersAgendaQueryVariables
  ): Observable<Partial<Dossier[]>> {
    return this.searchDossiersAgendaGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.searchDossiersAgenda) {
            return response.data.searchDossiersAgenda as unknown as Partial<
              Dossier[]
            >;
          }
          return [];
        })
      );
  }

  abandonDossier(params: AbandonDossierMutationVariables): Observable<{
    abandonned: boolean;
    dossierForContractManager?: any;
    dossier: Partial<Dossier>;
  }> {
    return this.abandonDossierGQL.mutate(params).pipe(
      map(
        (response) =>
          response.data?.abandonDossier as unknown as {
            abandonned: boolean;
            dossierForContractManager?: any;
            dossier: Partial<Dossier>;
          }
      )
    );
  }

  sendDossierConcurencyAction(
    params: MutationDossierAgendaConcurencyArgs
  ): Observable<boolean> {
    return this.sendDossierConcurencyActGQL.mutate(params).pipe(
      map((response) => {
        return response.data.dossierAgendaConcurency as boolean;
      })
    );
  }

  // NOTE - We use fetch instead of httpClient since it's the only way we can have the keepalive option
  registerAppClose(
    wait: boolean,
    dossierId: string,
    requestInfos: {
      accessToken: string;
      requestId: string;
      clientKey: string;
    }
  ) {
    return fetch(this.registerAppCloseURL, {
      method: 'POST',
      headers: {
        requestName: 'auth-http registerAppClose',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + requestInfos.accessToken,
        requestId: requestInfos.requestId,
        clientKey: requestInfos.clientKey,
      },
      body: JSON.stringify({ wait, state: 'CLOSING', dossierId }),
      keepalive: true,
    });
  }

  registerAppCloseCancelled(
    dossierId: string,
    requestInfos: {
      accessToken: string;
      requestId: string;
      clientKey: string;
    }
  ) {
    return fetch(this.registerAppCloseURL, {
      method: 'POST',
      headers: {
        requestName: 'auth-http registerAppCloseCancelled',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + requestInfos.accessToken,
        requestId: requestInfos.requestId,
        clientKey: requestInfos.clientKey,
      },
      body: JSON.stringify({ state: 'RESET', dossierId }),
      keepalive: true,
    });
  }

  notifyCustomer(dossierId: string) {
    return this.httpClient.post<Partial<Dossier>>(
      this.notifyCustomerURL,
      { dossierId },
      { headers: { requestName: 'dossier-http notifyCustomer' } }
    );
  }

  sendCustomerAccessEmail(dossierId: string) {
    return this.httpClient.post<Partial<Dossier>>(
      this.sendCustomerAccessEmailUrl,
      { dossierId },
      { headers: { requestName: 'dossier-http notifyCustomer' } }
    );
  }

  saveChatComments(
    params: SaveChatCommentsMutationVariables
  ): Observable<Dossier> {
    return this.saveChatCommentsGQL.mutate(params).pipe(
      map((response) => {
        return response.data.saveChatComments as Dossier;
      })
    );
  }
}
